import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HookInputProps } from 'types/hooks';
import { PromoAuditLogRow } from 'types/tpo/auditLog';

const errorMessage: string = 'Something went wrong!';

interface AuditLogOutput {
    loading: boolean;
    error: any;
    data: PromoAuditLogRow[] | null;
    count: number;
}

function useFetchPromoAuditLog({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
}: HookInputProps): AuditLogOutput {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<PromoAuditLogRow[] | null>(null);
    const [count, setCount] = useState(0);

    const isFirstPage = variables?.offset === 0;

    const onFetchError = useCallback((err: any = errorMessage) => {
        setData(null);
        setCount(0);
        setLoading(false);
        setError(err);
    }, []);

    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            if (isFirstPage) {
                setData(null);
                setError(null);
                setCount(0);
            }
            setLoading(true);

            try {
                const response = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return response;
            } catch (err) {
                onFetchError(err);
            }
        },
        ...queryConfig,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    useEffect(() => {
        if (!apiLoading && apiError && !error) {
            if (onError) {
                onError(apiError);
            } else {
                onFetchError(apiError);
            }
        }
    }, [apiLoading, apiError, error, onError, onFetchError]);

    useEffect(() => {
        if (!apiSuccess || apiData === undefined) return;
        if (error) setError(null);

        const payload = apiData?.data;
        if (onComplete) {
            onComplete(payload);
            return;
        }

        try {
            const rows: PromoAuditLogRow[] = payload?.data ?? [];
            const total: number = payload?.total_count ?? 0;
            setData((prev) => (!isFirstPage && Array.isArray(prev) ? [...prev, ...rows] : rows));
            setCount(total);
        } catch (err) {
            console.error(errorMessage, err);
            onFetchError(err);
        } finally {
            setLoading(false);
        }
    }, [apiData, apiSuccess, error, isFirstPage, onComplete, onFetchError]);

    return { loading, error, data, count };
}

export { useFetchPromoAuditLog };
